import { ReactNode, useState } from "react";
import { SectionKey } from "containers/project-boards/types";
import { ChevronIcon } from "./icons";

const COLLAPSE_KEY = "project-boards-collapsed";

function readCollapsed(key: SectionKey): boolean {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    if (!raw) return false;
    return !!(JSON.parse(raw) as Record<string, boolean>)[key];
  } catch {
    return false;
  }
}

function writeCollapsed(key: SectionKey, collapsed: boolean): void {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    map[key] = collapsed;
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

interface BoardSectionProps {
  sectionKey: SectionKey;
  icon: ReactNode;
  title: string;
  count?: number;
  children: ReactNode;
}

export default function BoardSection({
  sectionKey,
  icon,
  title,
  count,
  children,
}: BoardSectionProps) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(sectionKey));

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    writeCollapsed(sectionKey, next);
  };

  return (
    <section className="mb-7">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="mb-3 flex w-full items-center gap-2 text-left"
      >
        <span className="text-[var(--p-color-text-secondary)]">{icon}</span>
        <h2 className="text-[15px] font-semibold text-[var(--p-color-text)]">
          {title}
        </h2>
        {typeof count === "number" && count > 0 && (
          <span className="rounded bg-[var(--p-color-bg-surface-tertiary)] px-1.5 py-0.5 text-[11px] font-medium text-[var(--p-color-text-secondary)]">
            {count}
          </span>
        )}
        <ChevronIcon
          className={`ml-1 text-[var(--p-color-text-secondary)] transition-transform ${
            collapsed ? "-rotate-90" : ""
          }`}
        />
      </button>
      {!collapsed && children}
    </section>
  );
}
